<template>
  <div>
    <v-row>
      <v-col>
        <custom-geo-search @selectLocation="handleSetLocation" />
      </v-col>
      <v-spacer></v-spacer>
      <v-col class="d-flex align-center"
        ><span>CTRL + click to select a location.</span></v-col
      >
      <v-col class="text-right">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              :disabled="!markerOnMap"
              @click="removeMarker"
              v-on="on"
            >
              <v-icon color="primary">mdi-map-marker-remove</v-icon>
            </v-btn>
          </template>
          <span>Remove marker</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn icon @click="refreshMap" v-on="on">
              <v-icon color="primary">mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Refresh map</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <l-map
      ref="map"
      :zoom="3"
      :center="center"
      style="width: 100%; height: 500px; position: relative"
      @click="handleMapClick"
    >
      <div v-if="selectedLocation" class="location-info white--text">
        <h3>Location Information</h3>
        <ul>
          <li>
            <strong>Country:</strong>
            <span
              :class="{
                'found-text': selectedLocation.country,
                'unknown-text': !selectedLocation.country,
              }"
              >{{ selectedLocation.country || "not found" }}</span
            >
          </li>
          <li>
            <strong>City:</strong>
            <span
              :class="{
                'found-text': selectedLocation.city,
                'unknown-text': !selectedLocation.city,
              }"
              >{{ getCity(selectedLocation.city) }}</span
            >
          </li>
          <li>
            <strong>Street:</strong>
            <span
              :class="{
                'found-text': selectedLocation.street,
                'unknown-text': !selectedLocation.street,
              }"
              >{{ selectedLocation.street || "not found" }}</span
            >
          </li>
          <li>
            <strong>Street Number:</strong>
            <span
              :class="{
                'found-text': selectedLocation.housenumber,
                'unknown-text': !selectedLocation.housenumber,
              }"
              >{{ selectedLocation.housenumber || "not found" }}</span
            >
          </li>
          <li>
            <strong>Postal Code:</strong>
            <span
              :class="{
                'found-text': selectedLocation.postcode,
                'unknown-text': !selectedLocation.postcode,
              }"
              >{{ selectedLocation.postcode || "not found" }}</span
            >
          </li>
        </ul>
      </div>
      <l-tile-layer
        url="http://{s}.tile.osm.org/{z}/{x}/{y}.png"
      ></l-tile-layer>
      <l-marker
        v-for="(marker, index) in markers"
        :key="index"
        :lat-lng="marker.latlng"
        :draggable="true"
        @dragend="handleMarkerDrag($event)"
      >
        <l-popup :open="true">{{ marker.label }}</l-popup>
      </l-marker>
    </l-map>
    <v-row class="mt-1">
      <v-col cols="8">
        <v-row>
          <v-col cols="5">
            <v-text-field
              v-model.number="latitude"
              label="Latitude"
              type="number"
              :rules="[numeric, centroidLatitudeRules]"
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="5">
            <v-text-field
              v-model.number="longitude"
              label="Longitude"
              type="number"
              :rules="[numeric, centroidLongitudeRules]"
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="2">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  elevation="0"
                  color="primary"
                  small
                  fab
                  @click="findCoordinates"
                  v-on="on"
                >
                  <v-icon>mdi-magnify</v-icon>
                </v-btn>
              </template>
              <span>Find coordinates</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="4" class="text-right">
        <v-btn :disabled="!markerOnMap" @click="savePosition" color="success"
          >Done</v-btn
        >
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { Icon } from "leaflet";
import axios from "axios";
import CustomGeoSearch from "./CustomGeoSearch.vue";

//fix for marker visibility
delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  components: { CustomGeoSearch },
  props: {
    latitudeParent: Number,
    longitudeParent: Number,
    isOpen: Boolean,
  },
  data() {
    return {
      center: [51.505, -0.159],
      markers: [],
      marker: null,
      latitude: this.latitudeParent,
      longitude: this.longitudeParent,
      address: null,
      geocoding: null,
      selectedLocation: null,
      centroidLatitudeRules: (v) =>
        (v >= -90 && v <= 90) || "Centroid latitude must be between -90 and 90",
      centroidLongitudeRules: (v) =>
        (v >= -180 && v <= 180) ||
        "Centroid longitude must be between -180 and 180",
      numeric: (v) => Number.isFinite(v) || "Please provide a number",
    };
  },
  mounted() {
    setTimeout(() => {
      this.refreshMap();
      if (this.longitude) this.findAddress(this.latitude, this.longitude);
    }, 0);
  },
  methods: {
    getCity(city) {
      if (city) {
        if (city.slice(0, 4) === "City") return city.slice(8);
        else return city;
      } else return "not found";
    },
    handleMapClick(e) {
      if (e.originalEvent.ctrlKey) {
        const { lat, lng } = e.latlng;
        this.findAddress(lat, lng);
      }
    },
    findAddress(lat, lng) {
      axios
        .get(
          "https://nominatim.openstreetmap.org/reverse?format=geocodejson&lat=" +
            lat +
            "&lon=" +
            lng
        )
        .then((response) => {
          this.geocoding = response.data.features[0].properties.geocoding;
          this.setMarker(lat, lng, this.geocoding.label);
          this.setLocationInfo(this.geocoding);
          this.center = [lat, lng];
        })
        .catch((error) => {
          console.log(error);
          this.setMarker(lat, lng, "Unable to find address");
          this.setLocationInfo({});
        });
    },

    setMarker(lat, lng, address) {
      const newMarker = {
        latlng: [lat, lng],
        label: address,
      };

      this.markers = [];
      this.markers.push(newMarker);
      this.marker = newMarker;
      this.setCurrentPosition(lat, lng);
    },
    setLocationInfo(location) {
      const { country, city, street, housenumber, postcode } = location;
      this.selectedLocation = {
        country,
        city,
        street,
        housenumber,
        postcode,
      };
    },
    handleMarkerDrag(e) {
      const { lat, lng } = e.target._latlng;
      this.findAddress(lat, lng);
      this.setCurrentPosition(lat, lng);
    },
    setCurrentPosition(lat, lng) {
      this.latitude = parseFloat(lat);
      this.longitude = parseFloat(lng);
    },
    savePosition() {
      this.$emit(
        "savePosition",
        [this.latitude, this.longitude],
        this.geocoding
      );
    },
    removeMarker() {
      this.markers = [];
      this.selectedLocation = null;
    },
    refreshMap() {
      this.$refs.map.mapObject.invalidateSize();
    },
    findCoordinates() {
      this.findAddress(this.latitude, this.longitude);
    },
    handleSetLocation(location) {
      this.findAddress(location.lat, location.lon);
    },
  },
  watch: {
    latitudeParent(newLatitude) {
      this.latitude = newLatitude;
    },
    longitudeParent(newLongitude) {
      this.longitude = newLongitude;
    },
    isOpen(open) {
      if (open) {
        this.removeMarker();
        this.refreshMap();
        this.latitude = this.latitudeParent;
        this.longitude = this.longitudeParent;
        if (this.latitude) this.findAddress(this.latitude, this.longitude);
      }
    },
  },
  computed: {
    markerOnMap() {
      return this.markers.length > 0;
    },
  },
};
</script>
<style>
.location-info {
  border: 1px solid #ccc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 15px;
  margin-top: 20px;
  z-index: 1000;
  position: absolute;
  bottom: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.8);
}

.location-info h3 {
  font-size: 18px;
  margin-bottom: 10px;
}

.location-info ul {
  list-style-type: none;
  padding: 0;
}

.location-info li {
  margin-bottom: 8px;
}

.location-info li strong {
  font-weight: bold;
  margin-right: 5px;
  display: inline-block;
  width: 120px;
}

.unknown-text {
  color: orangered;
  font-weight: bold;
}
.found-text {
  color: lightgreen;
  font-weight: bold;
}
</style>
